import React, {
  forwardRef,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import {
  FieldTypes,
  parseSelectOptions,
  generateFormFields,
} from "utils/formUtils";
import GenericMappingForm from "../GenericMappingForm";
import {
  isEmpty,
  uniqBy,
  compact,
  map,
  includes,
  forEach,
  isString,
  uniq,
} from "lodash";
import message from "components/CustomMessage";
import { Form, Button } from "antd";

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  projectList: [{ projectId: undefined }],
};

const DELIMITER = ";";

const MeterianMappingForm = forwardRef(function MeterianMappingForm(
  props,
  ref
) {
  const [mappedProjectList] = useState(() => {
    //using mappedProjectList to preserve the selected project when API fails
    if (props.record) {
      const { projectId, projectName } = props.record;
      return { projects: [{ id: projectId, name: projectName }] };
    }
    return { projects: [] };
  });
  const [branchOptions, setBranchOptions] = useState({});
  const [projects, setProjects] = useState([]);

  const [form] = Form.useForm();

  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [{ ...initialFormValues }];
  });

  const fetchProjects = useCallback(() => {
    const { promise, cancel } =
      ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
        login_id: props.loginConfigId,
      });
    promise
      .then((response) => {
        let { projects } = response;
        if (mappedProjectList["projects"].length > 0 && !isEmpty(projects)) {
          response.projects = uniqBy(
            [...projects, ...mappedProjectList["projects"]],
            "id"
          );
        }

        setProjects(response.projects);
      })
      .catch((err) => {
        message.error("Problem fetching projects");
      });
    return cancel;
  }, [props.loginConfigId, props.toolName, mappedProjectList]);

  useEffect(() => fetchProjects(), [fetchProjects]);

  const getExtraFieldsData = useCallback(
    (config) => {
      const { projectList } = config;
      const projectId = projectList.reduce((acc, cv) => {
        const { projectId, branch } = cv;
        branch.forEach((b) => acc.push(`${projectId};${b}`));
        return acc;
      }, []);
      const projectNames = projectList.reduce((acc, cv) => {
        const { projectId, branch } = cv;
        const projectName = projects.find((p) => p.id === projectId)?.name;
        branch.forEach((b) => acc.push(`${projectName};${b}`));
        return acc;
      }, []);
      return {
        projectId: projectId.join(),
        projectName: projectNames.join(),
      };
    },
    [projects]
  );

  const parseExtraFieldsDataFromResponse = useCallback(
    (config) => {
      const { projectId } = config;
      const projectIdArray = uniq(
        projectId
          .split(",")
          .filter((i) => i.includes(DELIMITER))
          .map((i) => i.split(DELIMITER)[0])
      );
      const branchArray = projectId.split(",").reduce((acc, curr) => {
        const [key, value] = curr.split(DELIMITER);
        acc[key] = acc[key] ? [...acc[key], value] : [value];
        return acc;
      }, {});
      let branchObj = {};
      projectIdArray.forEach((p) => {
        branchObj[p] = projects.find(
          (project) => project.id === p
        )?.otherProperties?.branches;
      });
      const projectList = projectIdArray.map((id, key) => {
        const projectName = projects.find((p) => p.id === id)?.name;
        return {
          projectId: id,
          projectName,
          branch: branchArray[id],
        };
      });

      setBranchOptions((branches) => ({
        ...branches,
        ...branchObj,
      }));

      return {
        projectList,
      };
    },
    [projects]
  );

  const onProjectChange = useCallback(
    (project, field, listField) => {
      const { name } = field;
      const configurations = form.getFieldValue("configurations");
      const currentProject =
        configurations[listField.name]?.projectList[name]?.projectId;
      setBranchOptions((branches) => ({
        ...branches,
        [currentProject]: projects.find((p) => p.id === currentProject)
          ?.otherProperties?.branches,
      }));
    },
    [form, projects]
  );

  const finalProjectOptions = useCallback(
    (projects, listField) => {
      const selected = compact(
        map(
          form.getFieldsValue().configurations?.[listField.name]?.projectList,
          (p) => p?.projectId
        )
      );
      return map(projects, (p) => ({
        ...p,
        disabled: includes(selected, p.id),
      }));
    },
    [form]
  );
  const projectListRender = useCallback(
    (listField) => {
      const __fields = [
        {
          name: "projectId",
          label: "Project",
          type: FieldTypes.SELECT,
          colSpan: 10,
          options: finalProjectOptions(projects, listField),
          required: true,
          props: {
            onChange: (project, field) =>
              onProjectChange(project, field, listField),
          },
        },
        {
          name: "branch",
          label: "Branch",
          type: FieldTypes.MULTI_SELECT,
          colSpan: 10,
          required: true,
          props: {
            getFormListOptions: (nestedListField) => {
              const configs = form.getFieldValue("configurations");
              const projectId =
                configs[listField.name]?.projectList[nestedListField]
                  ?.projectId;

              return parseSelectOptions(branchOptions[projectId]);
            },
          },
        },
        {
          name: "add",
          colSpan: 2,
          render: ({ remove, name, fields }) => {
            return (
              <Button
                disabled={fields.length === 1}
                type="dashed"
                onClick={() => {
                  remove(name);
                }}
              >
                -
              </Button>
            );
          },
        },
      ];

      return (
        <div className="m-b-sm">
          <Form.List name={[listField.name, "projectList"]}>
            {(fields, { add, remove }) => {
              return (
                <div>
                  {map(fields, (field) => {
                    return generateFormFields(
                      __fields,
                      10,
                      { ...field, add, remove, fields },
                      field.name
                    );
                  })}
                  <Button
                    size="small"
                    type="dashed"
                    onClick={() => add({ project: undefined })}
                  >
                    + Add Project
                  </Button>
                </div>
              );
            }}
          </Form.List>
        </div>
      );
    },
    [finalProjectOptions, form, branchOptions, onProjectChange, projects]
  );

  const extraFields = useMemo(
    () => [
      {
        name: (listField) => [listField.name, "projectList"],
        render: projectListRender,
      },
    ],
    [projectListRender]
  );

  const onValuesChange = useCallback(
    (values, ...args) => {
      if (values.configurations) {
        let isProjectChanged = false;
        const formValues = form.getFieldsValue();
        forEach(values.configurations, (configs, index) => {
          //look into SnykMappingFormV2 for explanation
          if (
            !configs ||
            !configs.projectList ||
            isString(configs.projectList)
          ) {
            return;
          }
          forEach(configs.projectList, (object, i) => {
            //look into SnykMappingFormV2 for explanation
            if (
              object &&
              object.projectId &&
              Object.keys(object).length === 1
            ) {
              isProjectChanged = true;
              formValues.configurations[index].projectList[i].branch =
                undefined;
            }
          });
        });
        if (isProjectChanged) {
          form.setFieldsValue(formValues);
        }
      }
    },
    [form]
  );

  return (
    <GenericMappingForm
      form={form}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
      onValuesChange={onValuesChange}
      {...props}
    />
  );
});

export default MeterianMappingForm;
